import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { theme, Collapse, Space, Typography } from 'antd';

import ListingsFilter from './components/ListingsFilter';
import ListingsList from './components/ListingsList';
import MetricsBackfillModal from './components/MetricsBackfillModal';
import SyncCoveragePanel from './components/SyncCoveragePanel';
import './Listings.css';
import { DEFAULT_FILTER_VALUES, FILTER_TYPES, isValidFilterType } from './constants/filterTypes';
import {
  fetchListings,
  handleSearchChange,
  handleFulfillmentChannelChange,
  handleStatusChange,
  handleSellerAccountChange,
  handleClearFilters,
  applyFilters,
  handlePaginationChange,
  handleExpandedRowsChange
} from './actions';
import { setFilters, setPagination } from './reducers';

const { Text } = Typography;

const parseNumberParam = (value, fallback) => {
  const numberValue = parseInt(value, 10);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback;
};

const parseJsonArrayParam = (value) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Invalid listings compoundFilters URL param:', error);
    return [];
  }
};

const normalizeSellerAccountParam = (searchParams) => {
  const values = searchParams.getAll('sellerAccountId').filter(Boolean);
  if (values.length > 1) return values;
  if (values.length === 1) return values[0];
  return DEFAULT_FILTER_VALUES[FILTER_TYPES.SELLER_ACCOUNT];
};

const parseListingsUrlState = (searchParams) => {
  const filters = {};
  const pagination = {};

  const searchText = searchParams.get('searchText') || searchParams.get('search');
  if (searchText) filters[FILTER_TYPES.SEARCH] = searchText;

  const searchScope = searchParams.get('searchScope');
  if (searchScope) filters[FILTER_TYPES.SEARCH_SCOPE] = searchScope;

  const fulfillmentChannel = searchParams.get('fulfillmentChannel');
  if (fulfillmentChannel) filters[FILTER_TYPES.FULFILLMENT_CHANNEL] = fulfillmentChannel;

  const status = searchParams.get('status');
  if (status) filters[FILTER_TYPES.STATUS] = status;

  const sellerAccount = normalizeSellerAccountParam(searchParams);
  if (sellerAccount !== DEFAULT_FILTER_VALUES[FILTER_TYPES.SELLER_ACCOUNT]) {
    filters[FILTER_TYPES.SELLER_ACCOUNT] = sellerAccount;
  }

  const compoundFilters = parseJsonArrayParam(searchParams.get('compoundFilters'));
  if (compoundFilters.length > 0) {
    filters[FILTER_TYPES.COMPOUND_FILTERS] = compoundFilters;
  }

  if (searchParams.get('salesPeriod')) {
    filters[FILTER_TYPES.SALES_PERIOD] = parseNumberParam(
      searchParams.get('salesPeriod'),
      DEFAULT_FILTER_VALUES[FILTER_TYPES.SALES_PERIOD]
    );
  }

  ['sortBy', 'sortOrder', 'groupSortBy', 'groupSortOrder'].forEach((key) => {
    const value = searchParams.get(key);
    if (value) filters[key] = value;
  });

  ['sortDays', 'groupSortDays'].forEach((key) => {
    const value = searchParams.get(key);
    if (value) filters[key] = parseNumberParam(value, DEFAULT_FILTER_VALUES[key]);
  });

  if (searchParams.get('page')) {
    pagination.current = parseNumberParam(searchParams.get('page'), 1);
  }
  if (searchParams.get('pageSize')) {
    pagination.pageSize = parseNumberParam(searchParams.get('pageSize'), 25);
  }

  return { filters, pagination };
};

const appendSellerAccountParams = (params, value) => {
  if (Array.isArray(value)) {
    value.filter(Boolean).forEach(accountId => params.append('sellerAccountId', accountId));
    return;
  }
  if (value && value !== DEFAULT_FILTER_VALUES[FILTER_TYPES.SELLER_ACCOUNT]) {
    params.set('sellerAccountId', value);
  }
};

const buildListingsSearchParams = (filters = {}, pagination = {}) => {
  const params = new URLSearchParams();

  if (filters.searchText) params.set('searchText', filters.searchText);
  if (filters.searchScope && filters.searchScope !== DEFAULT_FILTER_VALUES[FILTER_TYPES.SEARCH_SCOPE]) {
    params.set('searchScope', filters.searchScope);
  }
  if (
    filters.selectedFulfillmentChannel
    && filters.selectedFulfillmentChannel !== DEFAULT_FILTER_VALUES[FILTER_TYPES.FULFILLMENT_CHANNEL]
  ) {
    params.set('fulfillmentChannel', filters.selectedFulfillmentChannel);
  }
  if (filters.selectedStatus && filters.selectedStatus !== DEFAULT_FILTER_VALUES[FILTER_TYPES.STATUS]) {
    params.set('status', filters.selectedStatus);
  }
  appendSellerAccountParams(params, filters.selectedSellerAccount);

  if (Array.isArray(filters.compoundFilters) && filters.compoundFilters.length > 0) {
    params.set('compoundFilters', JSON.stringify(filters.compoundFilters));
  }

  if (filters.salesPeriod && filters.salesPeriod !== DEFAULT_FILTER_VALUES[FILTER_TYPES.SALES_PERIOD]) {
    params.set('salesPeriod', filters.salesPeriod);
  }
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);
  if (filters.sortDays) params.set('sortDays', filters.sortDays);
  if (filters.groupSortBy) params.set('groupSortBy', filters.groupSortBy);
  if (filters.groupSortOrder) params.set('groupSortOrder', filters.groupSortOrder);
  if (filters.groupSortDays) params.set('groupSortDays', filters.groupSortDays);

  if (pagination.current && pagination.current !== 1) params.set('page', pagination.current);
  if (pagination.pageSize && pagination.pageSize !== 25) params.set('pageSize', pagination.pageSize);

  return params;
};

const Listings = ({
  // Props từ mapStateToProps
  listings,
  loading,
  pagination,
  filters,
  expandedRowKeys,
  user, // Thêm user từ auth state
  // Props từ mapDispatchToProps (action creators đã được bind)
  fetchListings,
  handleSearchChange,
  handleFulfillmentChannelChange,
  handleStatusChange,
  handleSellerAccountChange,
  handleClearFilters,
  applyFilters,
  handlePaginationChange,
  handleExpandedRowsChange,
  setFilters,
  setPagination
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const currentPage = pagination.current;
  const pageSize = pagination.pageSize;

  const [isInitialized, setIsInitialized] = useState(false);
  const [metricsBackfillOpen, setMetricsBackfillOpen] = useState(false);
  const [syncCoverageOpen, setSyncCoverageOpen] = useState(false);

  // Initialize page on mount
  useEffect(() => {
    if (!isInitialized) {
      const urlState = parseListingsUrlState(searchParams);
      const nextPagination = {
        current: urlState.pagination.current || currentPage,
        pageSize: urlState.pagination.pageSize || pageSize
      };
      if (Object.keys(urlState.filters).length > 0) setFilters(urlState.filters);
      if (Object.keys(urlState.pagination).length > 0) setPagination(urlState.pagination);
      setIsInitialized(true);
      fetchListings(nextPagination.current, nextPagination.pageSize);
    }
  }, [isInitialized, fetchListings, currentPage, pageSize, searchParams, setFilters, setPagination]);

  useEffect(() => {
    if (!isInitialized) return;

    const nextParams = buildListingsSearchParams(filters, { current: currentPage, pageSize });
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [filters, currentPage, pageSize, isInitialized, searchParams, setSearchParams]);

  // Generic filter handlers with validation
  const filterHandlers = useMemo(() => ({
    [FILTER_TYPES.SEARCH]: handleSearchChange,
    [FILTER_TYPES.FULFILLMENT_CHANNEL]: handleFulfillmentChannelChange,
    [FILTER_TYPES.STATUS]: handleStatusChange,
    [FILTER_TYPES.SELLER_ACCOUNT]: handleSellerAccountChange
  }), [
    handleSearchChange,
    handleFulfillmentChannelChange,
    handleStatusChange,
    handleSellerAccountChange
  ]);

  // Generic filter change handler with validation
  const handleFilterChange = useCallback((filterType, value) => {
    if (!isValidFilterType(filterType)) {
      console.warn(`Invalid filter type: ${filterType}. Available types:`, Object.values(FILTER_TYPES));
      return;
    }

    const handler = filterHandlers[filterType];
    if (handler) {
      handler(value);
    } else {
      setFilters({ [filterType]: value });
    }
  }, [filterHandlers, setFilters]);

  const handleClearAllFilters = useCallback(() => {
    handleClearFilters();
    setPagination({ current: 1, pageSize });
    fetchListings(1, pageSize);
  }, [handleClearFilters, setPagination, fetchListings, pageSize]);

  const handleApplyFilters = useCallback(() => {
    applyFilters();
  }, [applyFilters]);

  const handleReload = useCallback((options = {}) => {
    fetchListings(currentPage, pageSize);
  }, [fetchListings, currentPage, pageSize]);

  const handleSyncCoverageCollapseChange = useCallback((keys) => {
    const open = Array.isArray(keys) ? keys.includes('syncCoverage') : keys === 'syncCoverage';
    setSyncCoverageOpen(open);
  }, []);

  // Memoize style objects to prevent re-creation
  const mainContainerStyle = useMemo(() => ({
    margin: '0px 10px 10px 10px',
    padding: '5px 16px 16px 16px',
    background: colorBgContainer,
    borderRadius: borderRadiusLG,
    minHeight: 280,
  }), [colorBgContainer, borderRadiusLG]);

  // Check permissions - tạm thời sử dụng inventory permissions
  // const canViewListings = user?.role?.permissions?.some(p => 
  //   p.code === 'inventory.read' || p.code === 'sku_item.read'
  // ) || false;

  // // If user doesn't have permission to view listings, show access denied
  // if (!canViewListings) {
  //   return (
  //     <Result
  //       status="403"
  //       title="403"
  //       subTitle="Bạn không có quyền truy cập vào trang này."
  //       extra={
  //         <Button type="primary" onClick={() => navigate('/')}>
  //           Quay về trang chủ
  //         </Button>
  //       }
  //     />
  //   );
  // }

  return (
    <div>
      <div
        style={{
          margin: '10px 10px 0px 10px',
        }}
      >
        <Collapse
          className="sync-coverage-collapse"
          size="small"
          activeKey={syncCoverageOpen ? ['syncCoverage'] : []}
          onChange={handleSyncCoverageCollapseChange}
          items={[
            {
              key: 'syncCoverage',
              label: (
                <Space size={8}>
                  <Text strong={syncCoverageOpen} type={syncCoverageOpen ? undefined : 'secondary'}>
                    Daily Sync Coverage
                  </Text>
                  {!syncCoverageOpen && <Text type="secondary">Daily job completion status by seller</Text>}
                </Space>
              ),
              children: syncCoverageOpen
                ? <SyncCoveragePanel onOpenMetricsBackfill={() => setMetricsBackfillOpen(true)} />
                : null
            }
          ]}
        />
      </div>
      <div
        style={{
          margin: '10px 10px 0px 10px',
        }}
      >
        <ListingsFilter
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearAllFilters}
          onApplyFilters={handleApplyFilters}
          onReload={handleReload}
          filters={filters}
          loading={loading}
        />
      </div>
      <div style={mainContainerStyle}>
        <ListingsList
          listings={listings}
          isLoading={loading}
          pagination={pagination}
          filters={filters}
          expandedRowKeys={expandedRowKeys}
          onExpandedRowsChange={handleExpandedRowsChange}
          onReload={handleReload}
        />
      </div>
      <MetricsBackfillModal
        open={metricsBackfillOpen}
        onCancel={() => setMetricsBackfillOpen(false)}
      />
    </div>
  );
};

// mapStateToProps - Chỉ lấy những state cần thiết
const mapStateToProps = (state) => {
  const listingsState = state.listings;
  const authState = state.auth;
  return {
    listings: listingsState.listings,
    loading: listingsState.loading,
    pagination: listingsState.pagination,
    filters: listingsState.filters,
    expandedRowKeys: listingsState.expandedRowKeys,
    user: authState.user, // Thêm user từ auth state
  };
};

// mapDispatchToProps - Sử dụng object notation để tự động bind actions
const mapDispatchToProps = {
  fetchListings,
  handleSearchChange,
  handleFulfillmentChannelChange,
  handleStatusChange,
  handleSellerAccountChange,
  handleClearFilters,
  applyFilters,
  handlePaginationChange,
  handleExpandedRowsChange,
  setFilters,
  setPagination
};

export default connect(mapStateToProps, mapDispatchToProps)(Listings);
